import { createSlice } from '@reduxjs/toolkit';
import { AddToCartFlow, ProductState } from 'store/product/types';
import setDesignProductReducer from './reducer/setDesignProductReducer';
import setEncPublicDesignIdReducer from './reducer/setEncPublicDesignIdReducer';
import setProductAttributesReducer from './reducer/setProductAttributesReducer';
import setProductIdReducer from './reducer/setProductIdReducer';
import setProductQuantityReducer from './reducer/setProductQuantityReducer';
import setInitialProductQuantityReducer from './reducer/setInitialProductQuantityReducer';
import cancelProductQuantityChangeReducer from './reducer/cancelProductQuantityChangeReducer';
import toggleProductAttributeReducer from './reducer/toggleProductAttributeReducer';
import setDeliveryDetailsReducer from './reducer/setDeliveryDetailsReducer';
import setDeliveryOptionsReducer from './reducer/setDeliveryOptionsReducer';
import setForcedProductPreviewReducer from './reducer/setForcedProductPreviewReducer';
import resetForcedProductPreviewsReducer from './reducer/resetForcedProductPreviewsReducer';
import setProductPreviewIndexReducer from './reducer/setProductPreviewIndexReducer';
import setDefaultProductPreviewsReducer from './reducer/setDefaultProductPreviewsReducer';
import setInitialProductFlagReducer from './reducer/setInitialProductFlagReducer';
import setPreventFromProceedReducer from './reducer/setPreventFromProceedReducer';
import selectBundleProductReducer from './reducer/selectBundleProductReducer';
import deselectBundleProductReducer from './reducer/deselectBundleProductReducer';
import setProductRecommendationReducer from './reducer/setProductRecommendationReducer';
import setAddingToCartFlowReducer from './reducer/setAddingToCartFlowReducer';
import setLastOrderItemIdsReducer from './reducer/setLastOrderItemIdsReducer';
import setMatchingProductsReducer from './reducer/setMathingProductsReducer';
import setFrameUpsellDataReducer from './reducer/setFrameUpsellDataReducer';
import setFrameUpsellSelectedFrameReducer from './reducer/setFrameUpsellSelectedFrameReducer';
import resetProductRecommendationsReducer from './reducer/resetProductRecommendationsReducer';
import resetDesignProductReducer from './reducer/resetDesignProductReducer';
import setOPPlusContentReducer from './reducer/setOPPlusContentReducer';
import resetOPPlusContentReducer from './reducer/resetOPPlusContentReducer';
import setMoreArtPrintsReducer from './reducer/setMoreArtPrintsReducer';
import resetMoreArtPrintsReducer from './reducer/resetMoreArtPrintsReducer';
import setPersonalisedProductDataReducer from './reducer/setPersonalisedProductDataReducer';
import setPersonalisedProductReducer from './reducer/setPersonalisedProductReducer';
import resetPersonalisedProductReducer from './reducer/resetPersonalisedProductReducer';
import setFetchedProductUidReducer from './reducer/setFetchedProductUidReducer';
import setOthersAlsoBoughtProductsReducer from './reducer/setOthersAlsoBoughtProductsReducer';
import setEveryItemOutOfStockReducer from './reducer/setEveryitemOutOfStockReducer';

export const productInitialState: ProductState = {
  designProduct: undefined,
  encPublicDesignId: undefined,
  productId: 0,
  isInitialProduct: true,
  productType: undefined,
  productAttributes: {},
  selectedBundleProducts: [],
  quantity: 1,
  initialQuantity: 1,
  deliveryDetails: undefined,
  deliveryOptions: undefined,
  forcedProductPreviews: {},
  productPreviewIndex: 0,
  defaultProductPreviews: [],
  preventFromProceed: false,
  addingToCartFlow: AddToCartFlow.idle,
  lastOrderItemId: undefined,
  productRecommendations: {},
  matchingProducts: [],
  othersAlsoBoughtProducts: [],
  everyItemOutOfStock: false,
};

export const slice = createSlice({
  name: 'product',
  initialState: productInitialState,
  reducers: {
    setDesignProductAction: setDesignProductReducer,
    setEncPublicDesignIdAction: setEncPublicDesignIdReducer,
    setProductIdAction: setProductIdReducer,
    setProductQuantityAction: setProductQuantityReducer,
    setInitialProductQuantityAction: setInitialProductQuantityReducer,
    cancelProductQuantityChangeAction: cancelProductQuantityChangeReducer,
    setProductAttributesAction: setProductAttributesReducer,
    toggleProductAttributeAction: toggleProductAttributeReducer,
    setDeliveryDetailsAction: setDeliveryDetailsReducer,
    setDeliveryOptionsAction: setDeliveryOptionsReducer,
    setForcedProductPreviewAction: setForcedProductPreviewReducer,
    resetForcedProductPreviewsAction: resetForcedProductPreviewsReducer,
    setProductPreviewIndexAction: setProductPreviewIndexReducer,
    setDefaultProductPreviewsAction: setDefaultProductPreviewsReducer,
    setInitialProductFlagAction: setInitialProductFlagReducer,
    setPreventFromProceedAction: setPreventFromProceedReducer,
    selectBundleProductAction: selectBundleProductReducer,
    deselectBundleProductAction: deselectBundleProductReducer,
    setProductRecommendationAction: setProductRecommendationReducer,
    setAddingToCartFlowAction: setAddingToCartFlowReducer,
    setLastOrderItemIdsAction: setLastOrderItemIdsReducer,
    setMatchingProductsAction: setMatchingProductsReducer,
    setFrameUpsellDataAction: setFrameUpsellDataReducer,
    setFrameUpsellSelectedFrameAction: setFrameUpsellSelectedFrameReducer,
    resetProductRecommendationsAction: resetProductRecommendationsReducer,
    resetDesignProductAction: resetDesignProductReducer,
    setOPPlusContentAction: setOPPlusContentReducer,
    resetOPPlusContentAction: resetOPPlusContentReducer,
    setMoreArtPrintsAction: setMoreArtPrintsReducer,
    resetMoreArtPrintsAction: resetMoreArtPrintsReducer,
    setPersonalisedProductDataAction: setPersonalisedProductDataReducer,
    setPersonalisedProductAction: setPersonalisedProductReducer,
    resetPersonalisedProductAction: resetPersonalisedProductReducer,
    setFetchedProductUidAction: setFetchedProductUidReducer,
    setOthersAlsoBoughtProductsAction: setOthersAlsoBoughtProductsReducer,
    setEveryItemOutOfStockAction: setEveryItemOutOfStockReducer,
  },
});

export const {
  setDesignProductAction,
  setEncPublicDesignIdAction,
  setProductIdAction,
  setProductQuantityAction,
  setInitialProductQuantityAction,
  cancelProductQuantityChangeAction,
  setProductAttributesAction,
  toggleProductAttributeAction,
  setDeliveryDetailsAction,
  setDeliveryOptionsAction,
  setForcedProductPreviewAction,
  resetForcedProductPreviewsAction,
  setProductPreviewIndexAction,
  setDefaultProductPreviewsAction,
  setInitialProductFlagAction,
  setPreventFromProceedAction,
  selectBundleProductAction,
  deselectBundleProductAction,
  setProductRecommendationAction,
  setAddingToCartFlowAction,
  setLastOrderItemIdsAction,
  setMatchingProductsAction,
  setFrameUpsellDataAction,
  setFrameUpsellSelectedFrameAction,
  resetProductRecommendationsAction,
  resetDesignProductAction,
  setOPPlusContentAction,
  resetOPPlusContentAction,
  setMoreArtPrintsAction,
  resetMoreArtPrintsAction,
  setPersonalisedProductDataAction,
  setPersonalisedProductAction,
  resetPersonalisedProductAction,
  setFetchedProductUidAction,
  setOthersAlsoBoughtProductsAction,
  setEveryItemOutOfStockAction,
} = slice.actions;

const product = slice.reducer;
export default product;
